import React, { useEffect, useMemo, useState, useContext } from "react";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaPlus, FaTrash, FaSave } from "react-icons/fa";
import PaymentLayoutWrapper from "../../components/PaymentLayoutWrapper";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

const BASE_URL = "https://nissi-india.com";

const ContractorEntryProject = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();
  const location = useLocation();

  // -------------------- localStorage helpers --------------------
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined && val !== "" ? val : fallback;
  };

  const toStr = (val, fallback = "") => {
    if (val === null || val === undefined) return fallback;
    const str = String(val).trim();
    return str === "" ? fallback : str;
  };

  const toNum = (val, fallback = 0) => {
    const num = Number(val);
    return Number.isFinite(num) ? num : fallback;
  };

  // -------------------- localStorage values --------------------
  const finYearId = useMemo(() => toStr(getLS("finYearId")), []);
  const finYear = useMemo(() => toStr(getLS("finYear")), []);
  const branchid = useMemo(
    () => toStr(getLS("branchId") || getLS("branchid")),
    [],
  );
  const branchtype = useMemo(
    () => toStr(getLS("branchType") || getLS("branchtype"), "HO"),
    [],
  );
  const branchname = useMemo(
    () => toStr(getLS("branchName") || getLS("branchname"), "HEAD-OFFICE"),
    [],
  );
  const userId = useMemo(
    () =>
      toNum(getLS("userId") || getLS("loginuserid") || getLS("loginUserId"), 0),
    [],
  );

  // -------------------- common headers --------------------
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchid,
      "x-branch": branchtype,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchid, branchtype, userId],
  );

  // edit mode data from listing page
  const editData = location?.state?.editData || null;
  const editMappingId = editData?.mappingId || null;
  const isEdit = !!editMappingId;

  // -----------------------------------------
  // State: master form
  // -----------------------------------------
  const [mappingCode, setMappingCode] = useState("");
  const [formData, setFormData] = useState({
    contractorId: "",
    projectId: "",
    worktypeId: "",
    calculationType: "Manual",
    perSqFtCost: "0",
    totSqFt: "0",
    totalAmount: "0",
    amountInWord: "",
  });

  // -----------------------------------------
  // State: DDLs
  // -----------------------------------------
  const [ddlContractors, setDdlContractors] = useState([]);
  const [ddlProjects, setDdlProjects] = useState([]);
  const [ddlWorkTypes, setDdlWorkTypes] = useState([]);

  // -----------------------------------------
  // State: grid details rows
  // -----------------------------------------
  const [detailRows, setDetailRows] = useState([]);

  const totalOfRows = useMemo(() => {
    return detailRows.reduce((sum, r) => sum + Number(r.totamount || 0), 0);
  }, [detailRows]);

  // -----------------------------------------
  // Helpers
  // -----------------------------------------
  const convertNumberToWords = (amount) => {
    const num = Math.floor(parseFloat(amount || 0));
    if (num === 0) return "ZERO";
    if (num < 0) return "minus " + convertNumberToWords(Math.abs(num));

    const unitsMap = [
      "ZERO",
      "ONE",
      "TWO",
      "THREE",
      "FOUR",
      "FIVE",
      "SIX",
      "SEVEN",
      "EIGHT",
      "NINE",
      "TEN",
      "ELEVEN",
      "TWELVE",
      "THIRTEEN",
      "FOURTEEN",
      "FIFTEEN",
      "SIXTEEN",
      "SEVENTEEN",
      "EIGHTEEN",
      "NINETEEN",
    ];

    const tensMap = [
      "ZERO",
      "TEN",
      "TWENTY",
      "THIRTY",
      "FORTY",
      "FIFTY",
      "SIXTY",
      "SEVENTY",
      "EIGHTY",
      "NINETY",
    ];

    let number = num;
    let words = "";

    if (number / 10000000 > 0) {
      words += convertNumberToWords(Math.floor(number / 10000000)) + " CRORE ";
      number %= 10000000;
    }
    if (number / 100000 > 0) {
      words += convertNumberToWords(Math.floor(number / 100000)) + " LAKH ";
      number %= 100000;
    }
    if (number / 1000 > 0) {
      words += convertNumberToWords(Math.floor(number / 1000)) + " THOUSAND ";
      number %= 1000;
    }
    if (number / 100 > 0) {
      words += convertNumberToWords(Math.floor(number / 100)) + " HUNDRED ";
      number %= 100;
    }

    if (number > 0) {
      if (words !== "") words += "AND ";
      if (number < 20) words += unitsMap[number];
      else {
        words += tensMap[Math.floor(number / 10)];
        if (number % 10) words += " " + unitsMap[number % 10];
      }
    }

    return words.trim();
  };

  const setField = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };

      if (
        ["perSqFtCost", "totSqFt", "calculationType"].includes(field) &&
        next.calculationType === "Sq.Ft"
      ) {
        const per = Number(next.perSqFtCost || 0);
        const sqft = Number(next.totSqFt || 0);
        const tot = (per * sqft).toFixed(2);
        next.totalAmount = tot;
        next.amountInWord = Number(tot) > 0 ? convertNumberToWords(tot) : "";
      }

      if (field === "totalAmount" && next.calculationType === "Manual") {
        const amt = Number(value || 0);
        next.amountInWord = amt > 0 ? convertNumberToWords(amt) : "";
      }

      if (field === "calculationType" && value === "Manual") {
        next.perSqFtCost = "0";
        next.totSqFt = "0";
      }

      return next;
    });
  };

  const findLabel = (arr, value) =>
    arr.find((x) => String(x.value) === String(value))?.label || "";

  // -----------------------------------------
  // API calls
  // -----------------------------------------
  const loadContractors = async () => {
    const res = await axios.get(
      `${BASE_URL}/api/contractorMapping/contractors`,
      {
        params: { branchid },
        headers: commonHeaders,
      },
    );
    setDdlContractors(Array.isArray(res.data?.data) ? res.data.data : []);
  };

  const loadProjects = async () => {
    const res = await axios.get(`${BASE_URL}/api/contractorMapping/projects`, {
      params: { branchtype, branchid },
      headers: commonHeaders,
    });
    setDdlProjects(Array.isArray(res.data?.data) ? res.data.data : []);
  };

  const loadWorkTypes = async (projectId) => {
    if (!projectId) {
      setDdlWorkTypes([]);
      return;
    }

    const res = await axios.get(`${BASE_URL}/api/contractorMapping/workTypes`, {
      params: { projectid: projectId, branchid },
      headers: commonHeaders,
    });

    setDdlWorkTypes(Array.isArray(res.data?.data) ? res.data.data : []);
  };

  const loadNextCode = async () => {
    const res = await axios.get(`${BASE_URL}/api/contractorMapping/nextCode`, {
      params: { branchid },
      headers: commonHeaders,
    });

    const code = res.data?.code || "1";
    setMappingCode(`${branchname}/${code}`);
  };

  const loadEditData = async (mappingId) => {
    const res = await axios.get(`${BASE_URL}/api/contractorMapping/getById`, {
      params: { mappingid: mappingId, branchid },
      headers: commonHeaders,
    });

    const master = res.data?.master;
    const details = Array.isArray(res.data?.details) ? res.data.details : [];

    if (!master) {
      toast.error("Edit record not found");
      return;
    }

    setMappingCode(master.fld_mappingcode || "");
    setFormData((prev) => ({
      ...prev,
      contractorId: master.fld_contractorid || "",
      projectId: master.fld_projectid || "",
      worktypeId: "",
      calculationType: "Manual",
      perSqFtCost: "0",
      totSqFt: "0",
      totalAmount: "0",
      amountInWord: "",
    }));

    setDetailRows(
      details.map((d, idx) => ({
        rowNo: idx + 1,
        worktypeid: d.fld_worktypeid,
        worktypename: d.fld_worktypename,
        type: d.fld_type,
        persqftcost: String(d.fld_persqftcost ?? "0"),
        totsqft: String(d.fld_totsqft ?? "0"),
        totamount: String(d.fld_totamount ?? "0"),
        totamountinwords: d.fld_totamountinwords || "",
      })),
    );

    await loadWorkTypes(master.fld_projectid);
  };

  // -----------------------------------------
  // Initial load
  // -----------------------------------------
  useEffect(() => {
    (async () => {
      try {
        if (!branchid) {
          toast.error("BranchId not found in localStorage!");
          return;
        }

        await Promise.all([loadContractors(), loadProjects()]);

        if (isEdit) {
          await loadEditData(editMappingId);
        } else {
          await loadNextCode();
        }
      } catch (err) {
        console.error(
          "Initial Load Error:",
          err?.response?.data || err.message,
        );
        toast.error(err?.response?.data?.message || err.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!formData.projectId) return;

    (async () => {
      try {
        await loadWorkTypes(formData.projectId);
        if (!isEdit) {
          setDetailRows([]);
        }
      } catch (err) {
        console.error(
          "Project Change Error:",
          err?.response?.data || err.message,
        );
        toast.error(err?.response?.data?.message || err.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.projectId]);

  // -----------------------------------------
  // Add row
  // -----------------------------------------
  const handleAddRow = () => {
    if (!formData.contractorId) return toast.error("Select Contractor Name");
    if (!formData.projectId) return toast.error("Select Project Name");
    if (!formData.worktypeId) return toast.error("Select Work Type");

    const exists = detailRows.some(
      (r) => String(r.worktypeid) === String(formData.worktypeId),
    );
    if (exists) return toast.error("This work type already added");

    if (formData.calculationType === "Sq.Ft") {
      const per = Number(formData.perSqFtCost || 0);
      const sqft = Number(formData.totSqFt || 0);
      if (per <= 0 || sqft <= 0) {
        return toast.error("Per Sq.Ft Cost & Tot Sq.Ft must be > 0");
      }
    } else {
      const amt = Number(formData.totalAmount || 0);
      if (amt <= 0) return toast.error("Total Amount must be > 0");
    }

    const workLabel = findLabel(ddlWorkTypes, formData.worktypeId);

    const row = {
      rowNo: detailRows.length + 1,
      worktypeid: Number(formData.worktypeId),
      worktypename: workLabel,
      type: formData.calculationType,
      persqftcost:
        formData.calculationType === "Sq.Ft"
          ? String(formData.perSqFtCost || "0")
          : "0",
      totsqft:
        formData.calculationType === "Sq.Ft"
          ? String(formData.totSqFt || "0")
          : "0",
      totamount: String(formData.totalAmount || "0"),
      totamountinwords: formData.amountInWord || "",
    };

    setDetailRows((prev) => [...prev, row]);

    setField("worktypeId", "");
    setField("perSqFtCost", "0");
    setField("totSqFt", "0");
    setField("totalAmount", "0");
    setField("amountInWord", "");

    toast.success("Work details added successfully!");
  };

  const removeRow = (index) => {
    setDetailRows((prev) => {
      const copy = [...prev];
      copy.splice(index, 1);
      return copy.map((r, i) => ({ ...r, rowNo: i + 1 }));
    });
  };

  // -----------------------------------------
  // Submit
  // -----------------------------------------
  const handleSubmit = async () => {
    try {
      if (!formData.contractorId) return toast.error("Select Contractor");
      if (!formData.projectId) return toast.error("Select Project");
      if (detailRows.length === 0) {
        return toast.error("No work Types Added in this contractor");
      }
      if (!userId) return toast.error("UserId not found in localStorage!");

      const payload = {
        branchid,
        branchname,
        loginuserid: Number(userId),
        contractorid: Number(formData.contractorId),
        projectid: Number(formData.projectId),
        details: detailRows.map((r) => ({
          worktypeid: Number(r.worktypeid),
          worktypename: r.worktypename,
          type: r.type,
          persqftcost: Number(r.persqftcost || 0),
          totsqft: Number(r.totsqft || 0),
          totamount: Number(r.totamount || 0),
          totamountinwords: r.totamountinwords || "",
        })),
      };

      if (isEdit) payload.mappingid = Number(editMappingId);

      const res = await axios.post(
        `${BASE_URL}/api/contractorMapping/save`,
        payload,
        { headers: commonHeaders },
      );

      toast.success(
        res.data?.message ||
          (isEdit ? "Updated Successfully" : "Saved Successfully"),
      );
      navigate("/PaymentVouchers/ContractorProjectListing");
    } catch (err) {
      console.error("Submit Error:", err?.response?.data || err.message);
      toast.error(err?.response?.data?.message || err.message);
    }
  };

  const handleBack = () =>
    navigate("/PaymentVouchers/ContractorProjectListing");

  // -----------------------------------------
  // UI
  // -----------------------------------------
  return (
    <PaymentLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">CONTRACTOR ENTRY</span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />
        <button
          onClick={handleBack}
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
            isDark
              ? "border-slate-600 text-slate-300 hover:bg-slate-800"
              : "border-slate-300 text-slate-700 hover:bg-slate-100"
          }`}
        >
          <FaArrowLeft /> Back
        </button>
        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
            <h1
              className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}
            >
              Contractor Project Entry {isEdit ? "(Edit)" : "(Add)"}
            </h1>

            <div className="flex items-center gap-3">
              <div
                className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                  isDark
                    ? "border-gray-700 bg-gray-800"
                    : "border-gray-200 bg-gray-50"
                }`}
              >
                <span className="opacity-80 mr-2">Mapping Code:</span>
                <span className="font-mono">{mappingCode || "-"}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
              >
                Contractor Name <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.contractorId}
                onChange={(e) => {
                  setField("contractorId", e.target.value);
                  setDetailRows([]);
                }}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                <option value="">Select</option>
                {ddlContractors.map((c, index) => (
                  <option
                    key={`${c.value || index}-${c.label || index}`}
                    value={c.value}
                  >
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
              >
                Contractor Code
              </label>
              <input
                type="text"
                readOnly
                value=""
                className={`w-full px-4 py-2 border rounded-lg ${
                  isDark
                    ? "bg-gray-800 border-gray-700 text-gray-300"
                    : "bg-gray-50 border-gray-200 text-gray-700"
                }`}
                placeholder="(Optional - display only)"
              />
              <p
                className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}
              >
                If you want contractor code auto-fill, backend needs a small
                API.
              </p>
            </div>

            <div className="md:col-span-2">
              <label
                className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
              >
                Project Name <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.projectId}
                onChange={(e) => {
                  setField("projectId", e.target.value);
                  setDetailRows([]);
                }}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                <option value="">Select</option>
                {ddlProjects.map((p, index) => (
                  <option
                    key={`${p.value || index}-${p.label || index}`}
                    value={p.value}
                  >
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div
            className={`mb-8 p-4 rounded-lg ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}
          >
            <h2
              className={`text-lg font-semibold mb-4 ${buttonClass} text-white p-2 rounded`}
            >
              WORK DETAILS / PAYMENT DETAILS
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  Work Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.worktypeId}
                  onChange={(e) => setField("worktypeId", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select</option>
                  {ddlWorkTypes.map((w, index) => (
                    <option
                      key={`${w.value || index}-${w.label || index}`}
                      value={w.value}
                    >
                      {w.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  Calculation Type
                </label>
                <div className="flex items-center gap-6">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="calculationType"
                      value="Manual"
                      checked={formData.calculationType === "Manual"}
                      onChange={(e) =>
                        setField("calculationType", e.target.value)
                      }
                      className="mr-2"
                    />
                    Manual
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="calculationType"
                      value="Sq.Ft"
                      checked={formData.calculationType === "Sq.Ft"}
                      onChange={(e) =>
                        setField("calculationType", e.target.value)
                      }
                      className="mr-2"
                    />
                    Sq.Ft
                  </label>
                </div>
              </div>

              {formData.calculationType === "Sq.Ft" && (
                <>
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                    >
                      Per Sq.Ft Cost <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.perSqFtCost}
                      onChange={(e) => setField("perSqFtCost", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                    >
                      Tot Sq.Ft <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.totSqFt}
                      onChange={(e) => setField("totSqFt", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                      min="0"
                      step="0.01"
                    />
                  </div>
                </>
              )}

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  Total Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.totalAmount}
                  onChange={(e) => setField("totalAmount", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all font-semibold ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-green-400"
                      : "bg-white border-gray-300 text-green-600"
                  }`}
                  min="0"
                  step="0.01"
                  readOnly={formData.calculationType === "Sq.Ft"}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  Amount In Words
                </label>
                <textarea
                  value={formData.amountInWord}
                  onChange={(e) => setField("amountInWord", e.target.value)}
                  rows={3}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                  placeholder="Amount will be converted to words automatically"
                />
              </div>
            </div>

            <div className="flex justify-center mb-2">
              <button
                onClick={handleAddRow}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add
              </button>
            </div>

            <div
              className={`mt-4 rounded-xl border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}
            >
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20">
                        #
                      </th>
                      <th className="px-3 py-3 text-left font-semibold border-r border-white/20">
                        Work Type
                      </th>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20">
                        Type
                      </th>
                      <th className="px-3 py-3 text-right font-semibold border-r border-white/20">
                        Per Sq.Ft
                      </th>
                      <th className="px-3 py-3 text-right font-semibold border-r border-white/20">
                        Tot Sq.Ft
                      </th>
                      <th className="px-3 py-3 text-right font-semibold border-r border-white/20">
                        Amount
                      </th>
                      <th className="px-3 py-3 text-left font-semibold border-r border-white/20">
                        Amount in Words
                      </th>
                      <th className="px-3 py-3 text-center font-semibold">
                        Remove
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {detailRows.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-3 py-5 text-center">
                          No work types added
                        </td>
                      </tr>
                    ) : (
                      detailRows.map((r, idx) => (
                        <tr
                          key={`${r.worktypeid}-${idx}`}
                          className={`${isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"}`}
                        >
                          <td
                            className={`px-3 py-2 text-center border-r ${isDark ? "border-gray-700" : "border-gray-200"}`}
                          >
                            {r.rowNo}
                          </td>

                          <td
                            className={`px-3 py-2 text-left border-r ${isDark ? "border-gray-700" : "border-gray-200"}`}
                          >
                            {r.worktypename}
                          </td>

                          <td
                            className={`px-3 py-2 text-center border-r ${isDark ? "border-gray-700" : "border-gray-200"}`}
                          >
                            {r.type}
                          </td>

                          <td
                            className={`px-3 py-2 text-right border-r ${isDark ? "border-gray-700" : "border-gray-200"}`}
                          >
                            {Number(r.persqftcost || 0).toFixed(2)}
                          </td>

                          <td
                            className={`px-3 py-2 text-right border-r ${isDark ? "border-gray-700" : "border-gray-200"}`}
                          >
                            {Number(r.totsqft || 0).toFixed(2)}
                          </td>

                          <td
                            className={`px-3 py-2 text-right font-mono border-r ${isDark ? "border-gray-700 text-green-400" : "border-gray-200 text-green-600"}`}
                          >
                            {Number(r.totamount || 0).toFixed(2)}
                          </td>

                          <td
                            className={`px-3 py-2 text-left border-r ${isDark ? "border-gray-700" : "border-gray-200"}`}
                          >
                            {r.totamountinwords}
                          </td>

                          <td className="px-3 py-2 text-center">
                            <button
                              onClick={() => removeRow(idx)}
                              className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-2"
                            >
                              <FaTrash /> Remove
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-3 text-right">
              <span
                className={`${isDark ? "text-gray-300" : "text-gray-700"} text-sm font-semibold mr-2`}
              >
                Total of Rows:
              </span>
              <span
                className={`font-mono text-sm ${isDark ? "text-green-400" : "text-green-600"}`}
              >
                {totalOfRows.toFixed(2)}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap justify-end items-center gap-6">
            <button
              onClick={handleSubmit}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
            >
              <FaSave /> {isEdit ? "Update" : "Submit"}
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </PaymentLayoutWrapper>
  );
};

export default ContractorEntryProject;
